import { useEffect, useMemo, useState } from "react";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import IBreadcrumbs from "@models/IBreadcrumbs";
import OwTable from "@components/shared/OwTable/OwTable";
import { useQbdLogViewerColumns } from "@hooks/business_integrations/qbd/useQbdLogViewerColumns";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import IQBDGetQueueLogsDTO from "@models/DTO/IQBDGetQueueLogsDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import IQBDOnGetQueueLogsDTO from "@models/DTO/IQBDOnGetQueueLogsDTO";
import IQBD_QueueUI from "@models/IQBD_QueueUI";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import { OwSelectOptionFn } from "@components/shared/OwSelectOptionFn/OwSelectOptionFn";
import { IOption } from "@models/IOption";
import QBDQueueActionEnum from "@models/QBDQueueActionEnum";
import OwDateInput from "@components/shared/OwDateInput/OwDateInput";
import Moment from "moment";
import OwButton from "@components/shared/OwButton/OwButton";
import IQBDQueueLogsFilters from "@models/IQBDQueueLogsFilters";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {};

export const QuickBooksDesktopLogViewer = (props: Props) => {
    const { supplierModel, userModel, socket } = useAppContextNonNull();

    const crumbs: IBreadcrumbs[] = [
        { title: "business_integrations", url: "/business/integrations" },
        { title: "quickbooks_desktop_logs", url: "/business/integrations/qbd/logs" },
    ];

    const tableColumns = useQbdLogViewerColumns();

    const [dataSet, setDataSet] = useState<IQBD_QueueUI[]>([]);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchFilter, setSearchFilter] = useState<IQBDQueueLogsFilters>({});

    const typeOptions: IOption[] = useMemo(() => {
        const options: IOption[] = [
            { label: "Create", value: "create" },
            { label: "Read", value: "read" },
        ];

        return options;
    }, []);

    const actionOptions: IOption[] = useMemo(() => {
        const options: IOption[] = [
            { label: "Create Sales Order", value: QBDQueueActionEnum.CREATE_SALES_ORDER },
            { label: "Create Invoice", value: QBDQueueActionEnum.CREATE_INVOICE },
            { label: "Create Item", value: QBDQueueActionEnum.CREATE_CATALOG_ITEM },
            { label: "Create Customer", value: QBDQueueActionEnum.CREATE_CUSTOMER },
            { label: "Create Purchase Order", value: QBDQueueActionEnum.CREATE_PURCHASE_ORDER },
            { label: "Read Customers", value: QBDQueueActionEnum.READ_CUSTOMERS },
            { label: "Read Items", value: QBDQueueActionEnum.READ_PRODUCTS },
            { label: "Read Sales Orders", value: QBDQueueActionEnum.READ_SALES_ORDERS },
            { label: "Read Invoices", value: QBDQueueActionEnum.READ_INVOICES },
        ];
        return options;
    }, []);

    const statusOptions: IOption[] = useMemo(() => {
        const options: IOption[] = [
            { label: "not_started", value: "NOT_STARTED" },
            { label: "started", value: "STARTED" },
            { label: "success", value: "SUCCESS" },
            { label: "error", value: "ERROR" },
        ];

        return options;
    }, []);

    const getLogs = (filter: IQBDQueueLogsFilters) => {
        const payload: IQBDGetQueueLogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            offset,
            limit,
        };

        const filterString = getFilterString(filter);

        if (filterString) {
            payload.filter = filterString;
        }

        setIsDataFetching(true);

        socket.emit(EndpointsEnum.QBDGetQueueLogs, payload);
    };

    const onGetLogs = (data: IQBDOnGetQueueLogsDTO) => {
        setIsDataFetching(false);
        setIsInitialData(false);
        setDataSet(data.queueRows);
        setTotalRecords(data.totalRows);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.QBDOnGetQueueLogs, fn: onGetLogs }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        document.title = "Orderwerks >> QuickBooks Desktop Logs";

        getLogs(searchFilter);

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        getLogs(searchFilter);
    }, [limit, offset]);

    const paginationChanged = (offset: number, limit: number) => {
        setOffset(offset);
        setLimit(limit);
    };

    const handleChange = (e: any) => {
        const name = e.target.name;
        const value = e.target.value;

        const updatedSearchFilter = {
            ...searchFilter,
            [name]: value,
        };

        setSearchFilter(updatedSearchFilter);
    };

    const handleDateChange = (date: Moment.Moment) => {
        setSearchFilter({
            ...searchFilter,
            created: date.format("YYYY-MM-DD"),
        });
    };

    const getFilterString = (filter: IQBDQueueLogsFilters) => {
        const filterString = [];

        if (filter.type?.length) {
            filterString.push(`type=${filter.type}`);
        }

        if (filter.action?.length) {
            filterString.push(`action=${filter.action}`);
        }

        if (filter.order?.length) {
            filterString.push(`order=${filter.order}`);
        }

        if (filter.vendor?.length) {
            filterString.push(`vendor=${filter.vendor}`);
        }

        if (filter.catalog_item_name?.length) {
            filterString.push(`catalog_item_name=${filter.catalog_item_name}`);
        }

        if (filter.status?.length) {
            filterString.push(`status=${filter.status}`);
        }

        if (filter.created?.length && filter.created !== "Invalid date") {
            filterString.push(`created=${filter.created}`);
        }

        if (filterString.length > 0) {
            return "?" + filterString.join("&");
        } else {
            return "";
        }
    };

    const resetSearch = () => {
        setSearchFilter({
            type: "",
            action: undefined,
            order: "",
            vendor: "",
            catalog_item_name: "",
            status: "",
            created: "",
        });
        getLogs({});
    };

    return (
        <div className={"QuickBooksDesktopLogViewer"}>
            <Breadcrumbs crumbs={crumbs} />
            <OwTitle title={"quickbooks_desktop_logs"} tag={"h3"} />

            <SearchSection>
                <div className={"row"}>
                    <div className={"col"}>
                        <OwLabel title={"type"} />
                        <OwSelectOptionFn options={typeOptions} valueProp={"value"} titleProp={"label"} fn={handleChange} name={"type"} />
                    </div>
                    <div className={"col"}>
                        <OwLabel title={"action"} />
                        <OwSelectOptionFn
                            options={actionOptions}
                            valueProp={"value"}
                            titleProp={"label"}
                            fn={handleChange}
                            name={"action"}
                            selectedOption={searchFilter.action}
                            hasSelectedOptionProp={false}
                        />
                    </div>
                    <div className={"col"}>
                        <OwLabel title={"order"} />
                        <OwFnInput fn={handleChange} name={"order"} value={searchFilter.order} />
                    </div>
                    <div className={"col"}>
                        <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={supplierModel} />
                        <OwFnInput fn={handleChange} name={"vendor"} value={searchFilter.vendor} />
                    </div>
                    <div className={"col"}>
                        <OwLabel title={"catalog_item"} />
                        <OwFnInput fn={handleChange} name={"catalog_item_name"} value={searchFilter.catalog_item_name} />
                    </div>
                    <div className={"col"}>
                        <OwLabel title={"status"} />
                        <OwSelectOptionFn
                            options={statusOptions}
                            valueProp={"value"}
                            titleProp={"label"}
                            fn={handleChange}
                            name={"status"}
                            selectedOption={searchFilter.status}
                            hasSelectedOptionProp={false}
                            translateTitle={true}
                        />
                    </div>
                    <div className={"col"}>
                        <OwLabel title={"created"} />
                        <OwDateInput fn={handleDateChange} selectedDate={searchFilter.created} />
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <OwButton
                            title={"search"}
                            fn={() => {
                                getLogs(searchFilter);
                            }}
                        />
                        <OwButton className={"btn btn-danger ms-2"} title={"reset"} fn={resetSearch} />
                    </div>
                </div>
            </SearchSection>

            <div className="mt-2 table-responsive">
                <OwTable
                    columns={tableColumns}
                    dataSet={dataSet}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                    tableConfig={{
                        tableStriped: true,
                    }}
                />
            </div>

            <div className="mt-2">
                <PaginationBar offset={offset} limit={limit} totalRecords={totalRecords} onPaginationChanged={paginationChanged} />
            </div>
        </div>
    );
};
